import React, { ReactNodeArray, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { uxAnalyticsState } from '../../../modules/PageAnalytics/UXAnalytics';

import { ReactComponent as IconFillArrowDownSVG } from '../../../assets/icons/icon-fill-arrow-down.svg';
import { ReactComponent as IconAimSVG } from '../../../assets/icons/icon-aim.svg';
import Spinner from '../../UIComponents/Spinner';

import errorImg from '../../../assets/images/icon-noimg@3x.png';

// icon
const IconFillArrowDown = styled(IconFillArrowDownSVG)``;
const IconAim = styled(IconAimSVG)`
  width: 24px;
  height: 24px;
  path {
    fill: white;
  }
`;
const Component = styled.div`
  width: 100%;
  height: 100%;
  padding: 24px 24px 40px 24px;
  background: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`;

const HeaderTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderTitleText = styled.div`
  font-family: 'RobotoB', 'NotoKrB';
  font-size: 20px;
  line-height: normal;
  color: ${(props) => props.theme.colors.grey800};
  margin-bottom: 8px;
  height: 29px;
`;

const HeaderTitleDesc = styled.div`
  font-family: 'RobotoR', 'NotoKrR';
  font-size: 14px;
  line-height: normal;
  color: ${(props) => props.theme.colors.grey600};
  height: 20px;
`;

const HeaderSortButton = styled.button<{ $isAsc: boolean }>`
  display: flex;
  align-items: center;
  height: 17px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  svg {
    width: 10px;
    height: 5px;
    ${(props) => {
      if (props.$isAsc) {
        return css`
          transform: rotate(180deg);
        `;
      }
      return css``;
    }}
  }
`;

const Split = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.colors.grey200};
  margin-top: 16px;
  margin-bottom: 24px;
`;

const HeaderSortTitle = styled.div`
  font-family: 'RobotoR', 'NotoKrR';
  font-size: 12px;
  line-height: normal;
  color: ${(props) => props.theme.colors.grey500};
  margin-right: 4px;
`;

const Body = styled.div`
  /* flex: 1; */
  display: flex;
  justify-content: space-between;
`;

const BodySplit = styled.div`
  height: 100%;
  width: 1px;
  background-color: ${(props) => props.theme.colors.grey200};
`;

const LeftBody = styled.div`
  width: 50%;
  height: 100%;
  padding-right: 24px;
  display: flex;
  flex-direction: column;
`;

const RightBody = styled.div`
  width: 50%;
  height: 100%;
  padding-left: 24px;
  display: flex;
  flex-direction: column;
`;

const BodyItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
  &:last-child {
    margin-bottom: 0px;
  }
`;

const BodyItemRank = styled.div`
  font-family: 'RobotoM', 'NotoKrM';
  font-size: 14px;
  line-height: normal;
  color: ${(props) => props.theme.colors.grey800};
  height: 20px;
  margin-bottom: 8px;
`;

const BodyItemThumbnailWrapper = styled.div<{
  $isTarget: boolean;
  $isScroll: boolean;
  $isError: boolean;
}>`
  width: 100%;
  height: 100px;
  border-radius: 8px;
  border: solid 1px ${(props) => props.theme.colors.grey200};
  padding: 24px;
  margin-bottom: 16px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) => {
    if (props.$isScroll) {
      return css`
        margin-bottom: 40px;
        height: 203px;
      `;
    }
    return css``;
  }}
  ${(props) => {
    if (props.$isTarget) {
      return css`
        background-color: rgba(181, 151, 247, 0.2);
        border: solid 1px ${props.theme.colors.purple3};
      `;
    }
    return css``;
  }}
  ${(props) => {
    if (props.$isError) {
      return css`
        background-color: ${props.theme.colors.grey100};
      `;
    }
    return css``;
  }}
`;

const BodyItemThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const BodyItemThumbnailHover = styled.div`
  left: 0;
  top: 0;
  border-radius: 8px;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  cursor: pointer;
`;

const BodyItemDataWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const BodyItemDataItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 24px;
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0px;
  }
`;

const BodyItemDataItemTitle = styled.div`
  font-family: 'RobotoR', 'NotoKrR';
  font-size: 16px;
  line-height: normal;
  color: ${(props) => props.theme.colors.grey700};
`;
const BodyItemDataItemValue = styled.div`
  font-family: 'RobotoB', 'NotoKrB';
  font-size: 16px;
  line-height: normal;
  color: ${(props) => props.theme.colors.grey800};
`;

const SpinnerWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const DataError = styled.div`
  font-family: 'RobotoR', 'NotoKrR';
  font-size: 14px;
  line-height: 1;
  color: ${(props) => props.theme.colors.grey800};
`;

interface ItemProps {
  rank: number;
  title1Value?: number;
  title2Value: number;
  elementId: string;
  pageId: string;
  targetElementID: string;
  isActiveDataType: {
    click: boolean;
    move: boolean;
    mouseflow: boolean;
    scroll: boolean;
  };
  onSetTargetElementId: (targetElementId: string) => void;
  onSetTooltipInfo: (info: uxAnalyticsState['tooltipInfo']) => void;
}

const Item = ({
  rank,
  title1Value,
  title2Value,
  elementId,
  pageId,
  targetElementID,
  isActiveDataType,
  onSetTargetElementId,
  onSetTooltipInfo,
}: ItemProps) => {
  const [isError, setIsError] = useState(false);
  const [isThumbnailOver, setIsThumbnailOver] = useState(false);
  const [itemTitleInfo, setItemTitleInfo] = useState({
    title1: '클릭 횟수',
    title2: '클릭 비율',
    title1Type: '회',
    title2Type: '%',
  });

  useEffect(() => {
    if (isActiveDataType.click) {
      setItemTitleInfo({
        title1: '클릭 횟수',
        title2: '클릭 비율',
        title1Type: '회',
        title2Type: '%',
      });
    } else if (isActiveDataType.move) {
      setItemTitleInfo({
        title1: '평균 관심 시간',
        title2: '관심 비율',
        title1Type: '초',
        title2Type: '%',
      });
    } else if (isActiveDataType.scroll) {
      setItemTitleInfo({
        title1: '평균 주목 시간',
        title2: '주목 비율',
        title1Type: '초',
        title2Type: '%',
      });
    }
  }, [isActiveDataType]);
  return (
    <BodyItem>
      <BodyItemRank>{rank}위</BodyItemRank>
      <BodyItemThumbnailWrapper
        $isScroll={isActiveDataType.scroll}
        $isTarget={targetElementID === elementId}
        $isError={isError}
        onMouseEnter={() => {
          setIsThumbnailOver(true);
        }}
        onMouseLeave={() => {
          setIsThumbnailOver(false);
        }}
      >
        <BodyItemThumbnailImage
          src={`https://voda-media.nerdfactory.ai/${localStorage.getItem(
            'voda_tenant'
          )}/auto/${pageId}/${encodeURIComponent(elementId)}`}
          onError={(event) => {
            const target = event.target as HTMLImageElement;
            target.src = errorImg;
            setIsError(true);
          }}
          onLoad={() => {
            setIsError(false);
          }}
        />
        {isThumbnailOver ? (
          <BodyItemThumbnailHover
            onClick={() => {
              onSetTargetElementId(elementId);
              onSetTooltipInfo({
                pathId: elementId,
                title1: rank,
                title2: title1Value || 0,
                title3: title2Value,
              });
            }}
          >
            <IconAim />
          </BodyItemThumbnailHover>
        ) : null}
      </BodyItemThumbnailWrapper>
      <BodyItemDataWrapper>
        <BodyItemDataItem>
          <BodyItemDataItemTitle>{itemTitleInfo.title1}</BodyItemDataItemTitle>
          <BodyItemDataItemValue>
            {Math.floor((title1Value || 0) * 100) / 100}
            {itemTitleInfo.title1Type}
          </BodyItemDataItemValue>
        </BodyItemDataItem>
        <BodyItemDataItem>
          <BodyItemDataItemTitle>{itemTitleInfo.title2}</BodyItemDataItemTitle>
          <BodyItemDataItemValue>
            {title2Value} {itemTitleInfo.title2Type}
          </BodyItemDataItemValue>
        </BodyItemDataItem>
      </BodyItemDataWrapper>
    </BodyItem>
  );
};

interface UXRankCardProps {
  elementData: {
    x: number;
    y: number;
    width: number;
    height: number;
    depth: number;
    element_path: string;
    cnt?: number;
    duration?: number;
    rate: number;
    rank: number;
  }[];
  targetElementID: string;
  pageId: string;
  isActiveDataType: {
    click: boolean;
    move: boolean;
    mouseflow: boolean;
    scroll: boolean;
  };
  isLoading: boolean;
  onSetTargetElementId: (targetElementId: string) => void;
  onSetTooltipInfo: (info: uxAnalyticsState['tooltipInfo']) => void;
}

const UXRankCard = ({
  elementData,
  targetElementID,
  pageId,
  isActiveDataType,
  isLoading,
  onSetTargetElementId,
  onSetTooltipInfo,
}: UXRankCardProps) => {
  const [isAsc, setIsAsc] = useState(false);
  const [headerInfo, setHeaderInfo] = useState({
    title: '클릭 순위',
    desc: '링크 및 클릭만 집계합니다.',
  });
  const printLeftBody = useMemo(() => {
    const result: ReactNodeArray = [];
    if (isAsc && elementData.length % 2 === 0) {
      elementData.forEach((element, index) => {
        if (index % 2 !== 0) {
          result.push(
            <Item
              rank={index + 1}
              pageId={pageId}
              key={element.element_path}
              title1Value={
                isActiveDataType.click ? element.cnt : element.duration
              }
              title2Value={element.rate}
              elementId={element.element_path}
              targetElementID={targetElementID}
              isActiveDataType={isActiveDataType}
              onSetTargetElementId={onSetTargetElementId}
              onSetTooltipInfo={onSetTooltipInfo}
            />
          );
        }
      });
    } else {
      elementData.forEach((element, index) => {
        if (index % 2 === 0) {
          result.push(
            <Item
              pageId={pageId}
              key={element.element_path}
              rank={index + 1}
              title1Value={
                isActiveDataType.click ? element.cnt : element.duration
              }
              title2Value={element.rate}
              elementId={element.element_path}
              targetElementID={targetElementID}
              isActiveDataType={isActiveDataType}
              onSetTargetElementId={onSetTargetElementId}
              onSetTooltipInfo={onSetTooltipInfo}
            />
          );
        }
      });
    }
    if (!isAsc) {
      if (result.length > 5) {
        return result.splice(0, 5);
      }
      return result;
    }
    if (result.length > 5) {
      return result.reverse().splice(0, 5);
    }
    return result.reverse();
  }, [elementData, isAsc, targetElementID, isActiveDataType]);
  const printRightBody = useMemo(() => {
    const result: ReactNodeArray = [];
    if (isAsc && elementData.length % 2 === 0) {
      elementData.forEach((element, index) => {
        if (index % 2 === 0) {
          result.push(
            <Item
              rank={index + 1}
              pageId={pageId}
              key={element.element_path}
              title1Value={
                isActiveDataType.click ? element.cnt : element.duration
              }
              title2Value={element.rate}
              elementId={element.element_path}
              targetElementID={targetElementID}
              isActiveDataType={isActiveDataType}
              onSetTargetElementId={onSetTargetElementId}
              onSetTooltipInfo={onSetTooltipInfo}
            />
          );
        }
      });
    } else {
      elementData.forEach((element, index) => {
        if (index % 2 !== 0) {
          result.push(
            <Item
              pageId={pageId}
              key={element.element_path}
              rank={index + 1}
              title1Value={
                isActiveDataType.click ? element.cnt : element.duration
              }
              title2Value={element.rate}
              elementId={element.element_path}
              targetElementID={targetElementID}
              isActiveDataType={isActiveDataType}
              onSetTargetElementId={onSetTargetElementId}
              onSetTooltipInfo={onSetTooltipInfo}
            />
          );
        }
      });
    }
    if (!isAsc) {
      if (result.length > 5) {
        return result.splice(0, 5);
      }
      return result;
    }
    if (result.length > 5) {
      return result.reverse().splice(0, 5);
    }
    return result.reverse();
  }, [elementData, isAsc, targetElementID, isActiveDataType]);

  useEffect(() => {
    if (isActiveDataType.click) {
      setHeaderInfo({
        title: '클릭 순위',
        desc: '링크 및 클릭만 집계합니다.',
      });
    } else if (isActiveDataType.move) {
      setHeaderInfo({
        title: '관심 순위',
        desc: '사용자의 마우스 체류시간과 클릭량이 많을 수록 순위가 높습니다.',
      });
    } else if (isActiveDataType.mouseflow) {
      setHeaderInfo({
        title: '마우스 플로우',
        desc:
          '사용자의 마우스 커서 움직임을 추적해 사용자의 콘텐츠 관심 순위를 파악합니다.',
      });
    } else if (isActiveDataType.scroll) {
      setHeaderInfo({
        title: '스크롤',
        desc:
          '사용자의 마우스 커서 움직임을 추적해 사용자의 콘텐츠 관심 순위를 파악합니다.',
      });
    }
  }, [isActiveDataType]);
  return (
    <Component>
      <Header>
        <HeaderTitleWrapper>
          <HeaderTitleText>{headerInfo.title}</HeaderTitleText>
          <HeaderTitleDesc>{headerInfo.desc}</HeaderTitleDesc>
        </HeaderTitleWrapper>
        <HeaderSortButton
          $isAsc={isAsc}
          onClick={() => {
            setIsAsc(!isAsc);
          }}
        >
          <HeaderSortTitle>{isAsc ? '오름차순' : '내림차순'}</HeaderSortTitle>
          <IconFillArrowDown />
        </HeaderSortButton>
      </Header>
      <Split />
      <Body>
        {isLoading ? (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        ) : (
          <>
            {elementData.length === 0 ? (
              <SpinnerWrapper>
                <DataError>선택한 기간에 데이터가 없습니다.</DataError>
              </SpinnerWrapper>
            ) : (
              <>
                <LeftBody>{printLeftBody}</LeftBody>
                <BodySplit />
                <RightBody>{printRightBody}</RightBody>
              </>
            )}
          </>
        )}
      </Body>
    </Component>
  );
};

export default UXRankCard;
